import { ChangeEvent, useEffect, useState } from "react"
import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { useObjectActions } from "./useObjectActions"

export type LinkedStyleT = {
    property: string
    style: SimpleStyleT
}

/**
 * Styles set together through one shared field, Figma style — the four corners of a radius,
 * the four sides of a margin or a padding — with a toggle that splits them into their own
 * fields.
 *
 * Styles that already differ open split ("independent"), and the shared field shows nothing
 * ("Mixed") until a value is typed. Nothing is written just by opening or toggling — only a
 * value committed into a field changes the object.
 *
 * @param properties the linked style properties, in the order their own fields are shown
 */
export const useLinkedStyles = (
    object: AnySceneObjectT,
    properties: string[],
    handleSetValue: (property: string, value: unknown) => void
) => {
    const { updateObjectStyles } = useObjectActions()

    const styles: LinkedStyleT[] = properties.flatMap((property) => {
        const style = object.styles.find((candidate) => candidate.property === property)
        return style ? [{ property, style }] : []
    })
    const values = styles.map(({ style }) => String(style.value))
    const isMixed = new Set(values).size > 1
    const unit = styles[0]?.style.unit ?? "px"

    const [independent, setIndependent] = useState(isMixed)
    // another object selected: start from what its styles look like
    useEffect(() => setIndependent(isMixed), [object.id]) // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * All of them in ONE write — one undo step. Calls of the single-style handler would each
     * start from the same, stale `object` and only the last one would survive. A ticker's
     * handler writes by id without that problem, and must be used: it keeps the ticker's
     * timeline running.
     */
    const handleAllChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value === "" || (!isMixed && value === values[0])) return
        if (object.type === "ticker") {
            styles.forEach(({ property }) => handleSetValue(property, value))
        } else {
            updateObjectStyles(
                object,
                Object.fromEntries(styles.map(({ property }) => [property, value]))
            )
        }
    }

    /** One style of the group, from its own field. */
    const handleOneChange = (e: ChangeEvent<HTMLInputElement>) =>
        handleSetValue(e.target.name, e.target.value)

    return {
        styles,
        /** the shared value, or "" while the styles differ */
        sharedValue: isMixed ? "" : values[0] ?? "",
        isMixed,
        unit,
        independent,
        toggleIndependent: () => setIndependent((current) => !current),
        handleAllChange,
        handleOneChange,
    }
}
